import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy, } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Alert, Button, Card, Col, Flex, Grid, Row, Skeleton, Space, Tag, Typography, } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { TableSelect } from "@/components/TableSelect/TableSelect";
import { ButtonRenderer } from "@/components/TableSelect/renderers/ButtonRenderer";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
import RouteBuilderItem from "./RouteBuilderItem";
const { Text } = Typography;
import styles from "./RouteBuilder.module.less";
const createOrdersTableConfig = {
    columns: [
        {
            title: "Номер заказа",
            dataIndex: "orderNumber",
            key: "orderNumber",
            fixed: "left",
            width: 150,
        },
        {
            title: "Адрес",
            key: "address",
            ellipsis: true,
            render: (record) => record?.addressInfo?.getAddress() || "Не указан",
        },
        {
            title: "Время клиента",
            dataIndex: "scheduledDate",
            key: "scheduledDate",
            width: 160,
            render: (date) => (date ? formatDateAndTime(date) : "Не указано"),
        },
    ],
    rowKey: "id",
    scroll: { y: 400, x: 600 },
};
// TODO вынести в отдельный компонент
const DraggableListItem = ({ id, children, }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
        transition,
        opacity: isDragging ? 0.8 : 1,
    };
    return (_jsx("div", { ref: setNodeRef, style: style, ...attributes, children: _jsxs("div", { className: styles.listItem, children: [_jsx("div", { ...listeners, className: styles.draggable, children: _jsx(HolderOutlined, {}) }), children] }) }));
};
export const RouteBuilder = observer(({ store, startTime, routeId, disabled: disabledProp, onCancelOrder }) => {
    // DND сенсоры
    const sensors = useSensors(useSensor(PointerSensor));
    const hasBases = store.activeItems.filter((p) => !!p.idBase).length === 2;
    const disabled = disabledProp || store.isLoading || store.isCalculating;
    const screens = Grid.useBreakpoint();
    const isMobile = useMemo(() => !screens.md, [screens]);
    // Конфигурация таблицы баз
    const createBasesTableConfig = useMemo(() => ({
        columns: [
            {
                title: "Название",
                dataIndex: "name",
                key: "name",
                ellipsis: true,
                sorter: true,
            },
            {
                title: "Адрес",
                key: "address",
                ellipsis: true,
                render: (record) => record?.addressInfo?.getAddress() ||
                    "Не указан",
            },
            {
                title: "Добавить в маршрут",
                key: "actions",
                width: 120,
                render: (_, record) => {
                    return (_jsx(Button, { type: "link", onClick: () => store.addBase([record.id]), children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" }));
                },
            },
        ],
        rowKey: "id",
        scroll: { y: 400 },
    }), []);
    // Инициализация данных - только для существующих маршрутов
    // Для новых маршрутов (quickOrder) данные уже загружены через initFromQuickOrderByIds
    useEffect(() => {
        if (routeId) {
            store.init(routeId);
        }
    }, [store, routeId]);
    // Синхронизация startTime в store - авто-расчёт обрабатывается MobX reaction в store
    useEffect(() => {
        store.setStartTime(startTime);
    }, [store, startTime]);
    const handleRemoveItem = (itemId) => {
        if (disabled)
            return;
        store.removeItem(itemId);
        // Авто-расчёт сработает через MobX reaction в store
    };
    // DND обработчик
    const handleDragEnd = (event) => {
        if (disabled)
            return;
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = store.selectedItems.findIndex((item) => item.key === active.id);
            const newIndex = store.selectedItems.findIndex((item) => item.key === over?.id);
            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(store.selectedItems, oldIndex, newIndex);
                store.reorderItems(newOrder);
                // Авто-расчёт сработает через MobX reaction в store
            }
        }
    };
    // Рендер элемента списка маршрута
    // Объединяем заказы маршрута (routeOrders) с доступными заказами (ordersSelectStore.pagedData)
    const allOrders = [...store.routeOrders, ...store.ordersSelectStore.pagedData];
    const renderRouteItem = (item) => {
        const itemOrderStatus = item.orderStatus;
        const isItemDeleted = item.isDeleted === true ||
            itemOrderStatus === "CANCELLED" ||
            itemOrderStatus === "DISPATCHER_CANCELLED";
        // Soft-deleted (отменённые) точки показываем без drag и кнопки удаления
        if (disabled || isItemDeleted) {
            return (_jsx("div", { children: _jsx(RouteBuilderItem, { item: item, bases: store.basesSelectStore.pagedData, orders: allOrders, onCancelOrder: onCancelOrder }) }, item.key));
        }
        return (_jsxs(DraggableListItem, { id: item.key, children: [_jsx(RouteBuilderItem, { item: item, bases: store.basesSelectStore.pagedData, orders: allOrders, onCancelOrder: onCancelOrder }), _jsx(Button, { danger: true, size: "small", icon: _jsx(DeleteOutlined, {}), disabled: disabled, onClick: () => handleRemoveItem(item.key), children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] }, item.key));
    };
    // Показать загрузку данных
    if (store.isLoading) {
        return (_jsx(Card, { children: _jsx("div", { style: { textAlign: "center", padding: "40px 0" }, children: _jsx(Text, { type: "secondary", children: "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0434\u0430\u043D\u043D\u044B\u0435..." }) }) }));
    }
    return (_jsxs(Space, { direction: "vertical", size: "large", style: { width: "100%" }, children: [_jsxs(Text, { type: "secondary", children: ["\u0414\u043E\u0431\u0430\u0432\u044C\u0442\u0435 \u0431\u0430\u0437\u044B \u0438 \u0437\u0430\u043A\u0430\u0437\u044B \u0434\u043B\u044F \u0444\u043E\u0440\u043C\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430. \u0412\u0440\u0435\u043C\u044F \u043D\u0430\u0447\u0430\u043B\u0430:", " ", startTime ? formatDateAndTime(startTime) : "Не указано"] }), store.error && (_jsx(Alert, { message: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0440\u0430\u0441\u0447\u0435\u0442\u0430 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430", description: store.error, type: "error", showIcon: true, closable: true, onClose: () => store.clearError() })), store.validationMessage && (_jsx(Alert, { message: "\u041F\u0440\u043E\u0431\u043B\u0435\u043C\u0430 \u0441 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u043E\u043C", description: store.validationMessage, type: "warning", showIcon: true })), !disabled && (_jsxs(Row, { gutter: [16, 16], children: [_jsx(Col, { xs: 24, sm: 24, md: 12, children: _jsxs(Space, { direction: "vertical", size: "small", style: { width: "100%" }, children: [_jsx(Text, { strong: true, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0431\u0430\u0437\u0443" }), _jsx(TableSelect, { disabled: hasBases || disabled, placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0431\u0430\u0437\u0443 \u0438\u0437 \u0442\u0430\u0431\u043B\u0438\u0446\u044B", tableSelectStore: store.basesSelectStore, dataTableConfig: createBasesTableConfig, drawLabel: store.drawBaseLabel, onChange: (value) => {
                                        store.addBase(value);
                                        // Авто-расчёт сработает через MobX reaction в store
                                    }, drawerTitle: "\u0412\u044B\u0431\u043E\u0440 \u0431\u0430\u0437", drawerWidth: isMobile ? "100vw" : "60vw", renderComponent: ButtonRenderer, style: { width: "100%" }, footer: null, hideSelection: true })] }) }), _jsx(Col, { xs: 24, sm: 24, md: 12, children: _jsxs(Space, { direction: "vertical", size: "small", style: { width: "100%" }, children: [_jsx(Text, { strong: true, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0437\u0430\u043A\u0430\u0437" }), _jsx(TableSelect, { disabled: !hasBases || disabled, placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0437\u0430\u043A\u0430\u0437 \u0438\u0437 \u0442\u0430\u0431\u043B\u0438\u0446\u044B", tableSelectStore: store.ordersSelectStore, dataTableConfig: createOrdersTableConfig, drawLabel: store.drawOrderLabel, drawerTitle: "\u0412\u044B\u0431\u043E\u0440 \u0437\u0430\u043A\u0430\u0437\u043E\u0432", drawerWidth: isMobile ? "100vw" : "80vw", renderComponent: ButtonRenderer, style: { width: "100%" }, footer: _jsx(Flex, { justify: "flex-end", align: "center", gap: 16, style: { width: "100%", marginTop: 30 }, children: _jsx(Button, { type: "primary", onClick: () => {
                                                store.addOrders(store.ordersSelectStore.dataTableStore.selectedRowKeys.map(String));
                                                store.ordersSelectStore.setVisible(false);
                                                // Авто-расчёт сработает через MobX reaction в store
                                            }, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0437\u0430\u043A\u0430\u0437" }) }) })] }) })] })), store.isCalculating ? (_jsx(Skeleton, { active: true })) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: store.activeItems.map((item) => item.key), strategy: verticalListSortingStrategy, children: _jsx("div", { children: store.selectedItems.map((item) => renderRouteItem(item)) }) }) })), store.selectedItems.length > 0 && (_jsx(Card, { title: "\u0421\u0432\u043E\u0434\u043A\u0430 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430", size: "small", children: _jsxs(Row, { gutter: 16, children: [_jsxs(Col, { xs: 24, sm: 24, md: 6, children: [_jsx(Text, { type: "secondary", children: "\u0412\u0441\u0435\u0433\u043E \u0442\u043E\u0447\u0435\u043A:" }), _jsx("br", {}), _jsx(Text, { strong: true, children: store.activeItems.length })] }), _jsxs(Col, { xs: 24, sm: 24, md: 6, children: [_jsx(Text, { type: "secondary", children: "\u0411\u0430\u0437:" }), _jsx("br", {}), _jsx(Text, { strong: true, children: store.activeItems.filter((item) => item.idBase).length })] }), _jsxs(Col, { xs: 24, sm: 24, md: 6, children: [_jsx(Text, { type: "secondary", children: "\u0417\u0430\u043A\u0430\u0437\u043E\u0432:" }), _jsx("br", {}), _jsx(Text, { strong: true, children: store.activeItems.filter((item) => item.idOrder).length })] }), _jsxs(Col, { xs: 24, sm: 24, md: 6, children: [_jsx(Text, { type: "secondary", children: "\u0421\u0442\u0430\u0442\u0443\u0441:" }), _jsx("br", {}), _jsx(Tag, { color: store.isValidRoute ? "green" : "red", children: store.isValidRoute ? "Готов" : "Требует доработки" })] })] }) }))] }));
});
export default RouteBuilder;
